/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { IOffer, ITypeCartFields, OfferContext, getPriceAmountByCatalogId, getLabelValue } from '@adac-mp/shared';
import {
	HintMessage,
	Typography,
	CamperIcon,
	CarIcon,
	MotorCycleIcon,
	TollSectionInfo,
	DropdownArrowDown,
	getHintContent,
} from '../../index';
import { useViewport } from '../../hooks/useViewport';

interface IProductSummaryProps {
	cardIsOpen?: boolean;
	cartLabels?: ITypeCartFields;
	category?: { moduleDetail: string; moduleDetailId: number };
	children?: React.ReactNode;
	className?: string;
	deliveryEstimation?: string;
	icon: React.ReactNode;
	isCheckoutPage?: boolean;
	multipleProducts?: boolean;
	offer?: IOffer;
	oneDayVignetteValidityHint?: boolean;
	openProductKeyIndex?: number;
	showProductPrice?: boolean;
	showProductPriceWithVat?: boolean;
	simple?: boolean;
	stackIsHovered?: boolean;
	stacked?: boolean;
	style?: React.CSSProperties;
	systemOverlapping?: boolean;
	title?: string | React.ReactNode;
	handleToggle?: (key: string) => void;
	setStackIsHovered?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ProductSummary = ({
	cardIsOpen,
	cartLabels,
	category,
	children,
	className,
	deliveryEstimation,
	icon: Icon,
	isCheckoutPage = false,
	multipleProducts,
	offer,
	oneDayVignetteValidityHint = false,
	openProductKeyIndex = 0,
	showProductPrice = false,
	showProductPriceWithVat = false,
	simple = false,
	stackIsHovered = false,
	stacked = false,
	style = {},
	systemOverlapping,
	title,
	handleToggle = () => {},
	setStackIsHovered = () => {},
}: IProductSummaryProps) => {
	const { viewport } = useViewport();
	const { labels } = useContext(OfferContext);
	const [isExpanded, setIsExpanded] = useState(false);
	const [animate, setAnimate] = useState<boolean | undefined>(true);
	const isDesktopView = viewport === 'desktop';
	const isVignette = offer?.moduleTypeId === 2;
	const isFlexService = offer?.moduleTypeId === 4;

	useEffect(() => {
		setAnimate(cardIsOpen);
		if (cardIsOpen) {
			setIsExpanded(true);
		}
	}, [cardIsOpen]);

	const totalPrice =
		!isFlexService && offer?.priceComponents ? getPriceAmountByCatalogId(offer.priceComponents) : null;
	const renderDownTime = offer?.downtimes && offer?.downtimes.length > 0 && offer.downtimes[0].type === 'warn';

	const iconMap = {
		2: <CarIcon className="inline mr-1.5" />,
		3: <MotorCycleIcon className="inline mr-1.5" />,
		4: <CamperIcon className="inline mr-1.5" />,
	};
	const renderDownTimeInformation = () =>
		offer?.downtimes.map(({ id, message }) => (
			<HintMessage key={id} className="mx-1.5 mb-3.5" padding="small" hints={[{ tip: 'Hinweis:', message }]} />
		));

	const renderSystemOverlappingMessage = () => (
		<HintMessage
			className="mx-1.5 mb-3.5"
			padding="small"
			hints={getHintContent(
				'systemDuplicate',
				[
					{
						tip: 'Hinweis',
						message:
							'Es existiert bereits eine gültige Digitale Vignette mit dem selben Kennzeichen und Gültigkeitszeitraum. Bitte überprüfen Sie Ihre Eingaben.',
					},
				],
				cartLabels?.hints,
			)}
		/>
	);

	const renderOneDayVignetteValidityMessage = () => (
		<HintMessage
			className="mx-1.5 mb-3.5"
			padding="small"
			hints={getHintContent(
				'validity',
				[
					{
						tip: 'Hinweis:',
						message: 'Diese Vignette ist nur noch bis 23:59 Uhr gültig.',
					},
				],
				cartLabels?.hints,
			)}
		/>
	);

	return (
		<div
			key={`${offer?.moduleType} section-${offer?.serviceCatalogCode}`}
			data-cy={`${offer?.moduleType} section-${offer?.serviceCatalogCode}`}
			className={`w-full ease-in-out flex flex-col duration-500 rounded border border-solid border-transparent ${
				!stacked && multipleProducts && 'hover:border-gray-400'
			} ${className}`}
			style={style}>
			<div
				className={`flex flex-col gap-4 ${
					isCheckoutPage ? 'py-3 md:py-4' : 'py-3'
				} rounded-t bg-white border border-solid border-transparent ${stacked ? ' min-h-[138px]' : ''} ${
					stackIsHovered ? '!border-gray-400' : ''
				} ${stacked && !stackIsHovered ? 'border-b-gray-600 overflow-hidden border-b' : ''} ${
					multipleProducts ? 'cursor-pointer' : ''
				} ${(multipleProducts && !isExpanded) || stacked ? 'rounded-b' : ''}`}
				onClick={() => {
					handleToggle(`${offer?.serviceCatalogId}-${openProductKeyIndex}`);
					setStackIsHovered(false);
				}}
				onMouseEnter={() => {
					if (stacked) {
						setStackIsHovered(true);
					}
				}}
				onMouseLeave={() => {
					if (stacked) {
						setStackIsHovered(false);
					}
				}}>
				<div className="flex items-center gap-4 px-3">
					{isDesktopView && Icon}
					<div className="flex flex-col gap-1.5">
						{title ? (
							<>
								<Typography component="h3" variant="title" color="title" size="xl">
									{title}
								</Typography>
								{isVignette && category && (
									<div className="flex items-center">
										{iconMap[category?.moduleDetailId]}
										<Typography color="lightText">{category?.moduleDetail}</Typography>
									</div>
								)}
							</>
						) : (
							<Skeleton inline height="100%" width="100%" />
						)}
						{!simple && (
							<TollSectionInfo
								trips={offer?.trips}
								downtimes={!stacked ? offer?.downtimes : undefined}
								sectionName={offer?.sectionName}
								routeDirection={!stacked ? offer?.routeDirection : undefined}
								moduleTypeId={offer?.moduleTypeId}
								labels={labels}
							/>
						)}
						{deliveryEstimation && <Typography color="lightText">{deliveryEstimation}</Typography>}
						{isFlexService && (
							<Typography color="lightText">{cartLabels?.allTollRoutesUsable || ''}</Typography> //should be added in contentful
						)}
					</div>
					<div
						className={`flex-1 inline-flex justify-end ${multipleProducts ? 'gap-2' : 'gap-1.5 flex-col'}`}>
						{(showProductPrice || showProductPriceWithVat) && (
							<Typography
								className={`inline-flex ${
									showProductPrice && multipleProducts ? 'self-center' : 'self-end'
								}`}
								size="xlEven"
								variant="title"
								color="title">
								{totalPrice ? `${totalPrice}€` : <Skeleton inline height="100%" width="100%" />}
							</Typography>
						)}
						{!multipleProducts && showProductPriceWithVat && (
							<Typography className="inline-flex self-end whitespace-nowrap" color="lightText">
								{getLabelValue('mp.vat', labels) || 'inkl. MwSt.'}
							</Typography>
						)}
						{multipleProducts && !stacked && (
							<button
								className={`cursor-pointer transition-transform border-none bg-transparent duration-300 ${
									isExpanded && animate && 'rotate-180'
								}`}>
								<DropdownArrowDown />
							</button>
						)}
					</div>
				</div>
			</div>
			{multipleProducts && isExpanded && !stacked && (
				<div
					className={`bg-white rounded-b ${animate ? 'animate-fadeSlideInFast' : 'animate-fadeSlideOutFast'}`}
					onAnimationEnd={() => {
						if (!animate) {
							setIsExpanded(false);
						}
					}}>
					{offer?.routeDirection && (
						<HintMessage
							className={`mb-3.5 mx-1.5`}
							padding="small"
							hints={getHintContent('tollSectionSingleTrip', [], cartLabels?.hints)}
						/>
					)}
					{renderDownTime && renderDownTimeInformation()}
					{systemOverlapping && renderSystemOverlappingMessage()}
					{oneDayVignetteValidityHint && renderOneDayVignetteValidityMessage()}
					{children}
				</div>
			)}
			{!multipleProducts && children}
		</div>
	);
};

ProductSummary.displayName = 'ProductSummary';
